import {
  cmToIn,
  kgToPd,
  inToFtAndIn,
} from "../../../questionnaire/utils";

/**
 * Check if a field is a measurement unit parameter (height, weight)
 */
export const isMeasurementUnitParameter = (fieldName: string): boolean => {
  const measurementUnitParams = [
    "height",
    "current_weight",
    "target_weight",
  ];
  return measurementUnitParams.includes(fieldName);
};

/**
 * Format height value for display based on unitType
 * @param cmValue - Height in cm
 * @param unitType - 1 = metric (cm), 2 = imperial (ft/in)
 * @returns Formatted height string
 */
export const formatHeightDisplay = (
  cmValue: number,
  unitType: number | undefined
): string => {
  if (unitType === 2) {
    const totalInches = convertHeightToDisplay(cmValue, unitType);
    const { value1: ft, value2: inches } = inToFtAndIn(totalInches);
    return `${ft} ft ${inches} in`;
  }
  return `${cmValue} cm`;
};

/**
 * Convert weight from kg to display unit based on unitType
 * @param kgValue - Weight in kg
 * @param unitType - 1 = metric (kg), 2 = imperial (lbs)
 * @returns Weight in display unit
 */
export const convertWeightToDisplay = (
  kgValue: number,
  unitType: number | undefined
): number => {
  if (unitType === 2) {
    return kgToPd(kgValue); // Rounded by kgToPd
  }
  return kgValue; // Keep decimals for metric
};

/**
 * Convert weight from display unit to kg (metric) for storage
 * @param displayValue - Weight in display unit
 * @param unitType - 1 = metric (kg), 2 = imperial (lbs)
 * @returns Weight in kg
 */
export const convertWeightToMetric = (
  displayValue: number,
  unitType: number | undefined
): number => {
  if (unitType === 2) {
    // Store without rounding to preserve precision for accurate round-trip conversion
    return displayValue * 0.45359237;
  }
  return displayValue;
};

/**
 * Convert height from cm to display unit based on unitType
 * @param cmValue - Height in cm
 * @param unitType - 1 = metric (cm), 2 = imperial (in)
 * @returns Height in display unit
 */
export const convertHeightToDisplay = (
  cmValue: number,
  unitType: number | undefined
): number => {
  if (unitType === 2) {
    return cmToIn(cmValue); // Rounded by cmToIn
  }
  return cmValue; // Keep decimals for metric
};

/**
 * Convert height from display unit to cm (metric) for storage
 * @param displayValue - Height in display unit
 * @param unitType - 1 = metric (cm), 2 = imperial (in)
 * @returns Height in cm
 */
export const convertHeightToMetric = (
  displayValue: number,
  unitType: number | undefined
): number => {
  if (unitType === 2) {
    // Store without rounding to preserve precision for accurate round-trip conversion
    return displayValue * 2.54;
  }
  return displayValue;
};

/**
 * Get the weight unit label based on unitType
 * @param unitType - 1 = metric, 2 = imperial
 * @returns Unit label string
 */
export const getWeightUnit = (unitType: number | undefined): string => {
  return unitType === 2 ? "lbs" : "kg";
};

/**
 * Get the height unit label based on unitType
 * @param unitType - 1 = metric, 2 = imperial
 * @returns Unit label string
 */
export const getHeightUnit = (unitType: number | undefined): string => {
  return unitType === 2 ? "in" : "cm";
};
